import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/product.models';
import {
  AttendanceDto,
  CheckInRequest,
  CheckOutRequest,
  EmployeeSummaryDto,
  TodayEmployeeStatusDto,
} from '../models/attendance.models';
import { ApiService } from './api.service';

interface PagedResult<T> {
  items: T[];
  totalCount: number;
}

@Injectable({ providedIn: 'root' })
export class AttendanceService extends ApiService {
  private readonly endPoint = environment.apiUrl + '/api/Attendance';

  checkIn(request: CheckInRequest): Observable<ApiResponse<AttendanceDto>> {
    return this.http.post<ApiResponse<AttendanceDto>>(`${this.endPoint}/checkin`, request);
  }

  checkOut(request: CheckOutRequest): Observable<ApiResponse<AttendanceDto>> {
    return this.http.post<ApiResponse<AttendanceDto>>(`${this.endPoint}/checkout`, request);
  }

  getMyTodayStatus(): Observable<ApiResponse<AttendanceDto | null>> {
    return this.http.get<ApiResponse<AttendanceDto | null>>(`${this.endPoint}/my-today`);
  }

  getTodayStatus(storeId?: string): Observable<ApiResponse<TodayEmployeeStatusDto[]>> {
    let params = new HttpParams();
    if (storeId) params = params.set('storeId', storeId);
    return this.http.get<ApiResponse<TodayEmployeeStatusDto[]>>(`${this.endPoint}/today`, {
      params,
    });
  }

  registerFace(descriptor: string): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.endPoint}/register-face`, { descriptor });
  }

  getMyFaceDescriptor(): Observable<ApiResponse<string | null>> {
    return this.http.get<ApiResponse<string | null>>(`${this.endPoint}/my-face`);
  }

  getHistory(
    date?: string,
    storeId?: string,
    page = 1,
    pageSize = 20,
  ): Observable<ApiResponse<PagedResult<AttendanceDto>>> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (date) params = params.set('date', date);
    if (storeId) params = params.set('storeId', storeId);
    return this.http.get<ApiResponse<PagedResult<AttendanceDto>>>(this.endPoint, { params });
  }

  getMyHistory(
    fromDate?: string,
    toDate?: string,
    page = 1,
    pageSize = 30,
  ): Observable<ApiResponse<PagedResult<AttendanceDto>>> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);
    return this.http.get<ApiResponse<PagedResult<AttendanceDto>>>(`${this.endPoint}/my`, {
      params,
    });
  }

  getSummary(
    from: string,
    to: string,
    storeId?: string,
  ): Observable<ApiResponse<EmployeeSummaryDto[]>> {
    let params = new HttpParams().set('from', from).set('to', to);
    if (storeId) params = params.set('storeId', storeId);
    return this.http.get<ApiResponse<EmployeeSummaryDto[]>>(`${this.endPoint}/summary`, { params });
  }
}
